// src/pages/actividades/components/mobile/ActividadesMobile.tsx
import { useState, useEffect } from 'react';
import { Search, Star, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useMobileNavStore } from '@/stores/mobileNavStore';
import { MobileSearchBar, MobileListSkeleton, MobileEmptyState } from '@/components/layout/mobile';
import { ActividadCard } from './ActividadCard';
import { ActividadDetailSheet } from './ActividadDetailSheet';
import type { ActividadListItem, ActividadTab } from '@/types';

interface ActividadesMobileProps {
  actividades: ActividadListItem[];
  isLoading: boolean;
  activeTab: ActividadTab;
  onTabChange: (tab: ActividadTab) => void;
  contadores?: Record<string, number>;
  soloEnfoque: boolean;
  onToggleEnfoque: () => void;
  onCompletarActividad: (id: string) => void;
  onReabrirActividad: (id: string) => void;
  hideCompleted: boolean;
}

const TAB_OPTIONS: { key: ActividadTab; label: string }[] = [
  { key: 'atrasadas', label: 'Atrasadas' },
  { key: 'hoy', label: 'Hoy' },
  { key: 'manana', label: 'Mañana' },
  { key: 'proximos7', label: '7 días' },
  { key: 'esta_semana', label: 'Semana' },
  { key: 'todas', label: 'Todas' },
  { key: 'sin_fecha', label: 'Sin fecha' },
  { key: 'algun_dia', label: 'Algún día' },
  { key: 'terminadas', label: 'Terminadas' },
];

export function ActividadesMobile({
  actividades,
  isLoading,
  activeTab,
  onTabChange,
  contadores,
  soloEnfoque,
  onToggleEnfoque,
  onCompletarActividad,
  onReabrirActividad,
  hideCompleted,
}: ActividadesMobileProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedActividad, setSelectedActividad] = useState<ActividadListItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const { setNavigation } = useMobileNavStore();

  useEffect(() => {
    setNavigation({ title: 'Actividades' });
  }, [setNavigation]);

  // Filter by search
  const filtered = searchText
    ? actividades.filter((a) =>
        a.titulo.toLowerCase().includes(searchText.toLowerCase()) ||
        a.cliente_nombre?.toLowerCase().includes(searchText.toLowerCase())
      )
    : actividades;

  // Filter completed if needed
  const visible = hideCompleted
    ? filtered.filter((a) => a.estado !== 'COMPLETADA' && a.estado !== 'CANCELADA')
    : filtered;

  const handleToggleComplete = (id: string) => {
    const act = actividades.find((a) => a.id === id);
    if (!act) return;
    if (act.estado === 'COMPLETADA' || act.estado === 'CANCELADA') {
      onReabrirActividad(id);
    } else {
      onCompletarActividad(id);
    }
  };

  const handleTap = (id: string) => {
    const act = actividades.find((a) => a.id === id);
    if (act) {
      setSelectedActividad(act);
      setDetailOpen(true);
    }
  };

  const emptyMessage = searchText
    ? 'No se encontraron actividades'
    : `No hay actividades en "${TAB_OPTIONS.find((t) => t.key === activeTab)?.label || activeTab}"`;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-background">
        {/* Tab selector dropdown */}
        <div className="relative">
          <select
            value={activeTab}
            onChange={(e) => onTabChange(e.target.value as ActividadTab)}
            className="appearance-none bg-muted/50 text-sm font-medium pl-3 pr-7 py-1.5 rounded-lg border-0 focus:ring-2 focus:ring-primary"
          >
            {TAB_OPTIONS.map((tab) => (
              <option key={tab.key} value={tab.key}>
                {tab.label}
                {contadores?.[tab.key] != null ? ` (${contadores[tab.key]})` : ''}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>

        <div className="flex-1" />

        {/* Enfoque toggle */}
        <Button
          variant={soloEnfoque ? 'default' : 'ghost'}
          size="sm"
          className="h-8 px-2"
          onClick={onToggleEnfoque}
        >
          <Star className={cn('h-4 w-4', soloEnfoque && 'fill-current')} />
        </Button>

        {/* Search toggle */}
        <Button
          variant={searchOpen ? 'default' : 'ghost'}
          size="sm"
          className="h-8 px-2"
          onClick={() => {
            setSearchOpen(!searchOpen);
            if (searchOpen) setSearchText('');
          }}
        >
          <Search className="h-4 w-4" />
        </Button>
      </div>

      {/* Search bar (expandable) */}
      {searchOpen && (
        <MobileSearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Buscar actividad..."
        />
      )}

      {/* Card list */}
      <div className="flex-1 overflow-y-auto overscroll-y-contain">
        {isLoading ? (
          <MobileListSkeleton itemHeight="h-[72px]" />
        ) : visible.length === 0 ? (
          <MobileEmptyState message={emptyMessage} />
        ) : (
          <div>
            <div className="px-4 py-2 text-xs text-muted-foreground">
              {visible.length} actividad{visible.length !== 1 ? 'es' : ''}
            </div>
            {visible.map((actividad) => (
              <ActividadCard
                key={actividad.id}
                actividad={actividad}
                onTap={handleTap}
                onToggleComplete={handleToggleComplete}
                isSubtarea={!!actividad.padre_id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail Sheet */}
      <ActividadDetailSheet
        actividad={selectedActividad}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onComplete={onCompletarActividad}
        onReopen={onReabrirActividad}
      />
    </div>
  );
}
